// src/components/Cronoprogramma.js
import React, { useState, useEffect, useRef } from "react";
import { collection, doc, onSnapshot, setDoc, updateDoc, deleteDoc, addDoc, serverTimestamp, query, where, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import { Plus, Trash2, ChevronLeft, ChevronRight, MessageSquare, X, Edit2, Check, Printer } from "lucide-react";
import { toLocalDateStr } from "../utils/dateUtils";
import { dominioSito } from "../utils/fornitore";

const COLORI_FASE = [
  "#7c3aed","#2563eb","#059669","#d97706","#dc2626","#0891b2","#7c3aed","#db2777"
];

function addDays(date, n) {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}
function startOfWeek(date) {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  d.setHours(0,0,0,0);
  return d;
}
function formatDate(d) {
  return toLocalDateStr(d);
}
function parseDate(s) {
  return s ? new Date(s+"T00:00:00") : null;
}
function formatLabel(d, mode) {
  if (mode === "settimana") {
    return `${d.getDate()}/${d.getMonth()+1}`;
  }
  return `${d.getDate()}/${d.getMonth()+1}`;
}

export default function Cronoprogramma({ projectId, orgId, project, isAdmin, currentUser }) {
  // Commessa chiusa = sola consultazione, anche per chi sarebbe admin. Riapri la commessa per modificare.
  const commessaChiusa = project?.statoCommessa === "chiusa";
  const puoModificare = isAdmin && !commessaChiusa;
  const [fasi, setFasi] = useState([]);
  const [note, setNote] = useState({});
  const [modo, setModo] = useState("giorno"); // "settimana" | "giorno"
  const [viewStart, setViewStart] = useState(null);
  const [cols, setCols] = useState(12);
  const [editFase, setEditFase] = useState(null);
  const [notaAperta, setNotaAperta] = useState(null); // { faseId, data }
  const [notaTesto, setNotaTesto] = useState("");
  const [showAddFase, setShowAddFase] = useState(false);
  const [nuovaFase, setNuovaFase] = useState({ nome:"", inizio:"", fine:"", colore: COLORI_FASE[0], descrizione:"" });
  const [dragging, setDragging] = useState(null);
  const [editConsegna, setEditConsegna] = useState(false);
  const [nuovaDataConsegna, setNuovaDataConsegna] = useState("");
  const [orgInfo, setOrgInfo] = useState(null);
  const containerRef = useRef(null);

  // Data di consegna
  const dataConsegna = project?.dataFine ? parseDate(project.dataFine) : null;
  const dataConsegnaOriginale = project?.dataConsegnaOriginale ? parseDate(project.dataConsegnaOriginale) : null;
  const oggi = new Date(); oggi.setHours(0,0,0,0);
  const consegnaSupeata = dataConsegna && oggi > dataConsegna;
  const consegnaModificata = dataConsegnaOriginale && project?.dataFine !== project?.dataConsegnaOriginale;

  // Carica dati organizzazione
  useEffect(() => {
    if (!orgId) return;
    getDoc(doc(db,"organizations",orgId)).then(snap => { if(snap.exists()) setOrgInfo(snap.data()); });
  }, [orgId]);

  // Carica fasi
  useEffect(() => {
    if (!projectId) return;
    return onSnapshot(
      query(collection(db,"cronoprogrammaFasi"), where("projectId","==",projectId), where("orgId","==",orgId)),
      snap => {
        const list = snap.docs.map(d=>({id:d.id,...d.data()}))
          .sort((a,b)=>(a.inizio||"").localeCompare(b.inizio||""));
        setFasi(list);
      }
    );
  }, [projectId, orgId]);

  // Carica note
  useEffect(() => {
    if (!projectId) return;
    return onSnapshot(
      query(collection(db,"cronoprogrammaNote"), where("projectId","==",projectId)),
      snap => {
        const map = {};
        snap.docs.forEach(d => {
          const data = d.data();
          const key = `${data.faseId}_${data.data}`;
          if (!map[key]) map[key] = [];
          map[key].push({ id:d.id, ...data });
        });
        setNote(map);
      }
    );
  }, [projectId]);

  // Calcola viewStart iniziale
  useEffect(() => {
    if (viewStart) return;
    const dates = fasi.map(f => f.inizio).filter(Boolean).sort();
    const start = dates.length > 0 ? parseDate(dates[0]) :
      (project?.dataInizio ? parseDate(project.dataInizio) : new Date());
    setViewStart(modo === "settimana" ? startOfWeek(start) : start);
  }, [fasi, project]);

  // Colonne visibili
  const colonne = [];
  if (viewStart) {
    for (let i = 0; i < cols; i++) {
      const d = modo === "settimana"
        ? addDays(viewStart, i * 7)
        : addDays(viewStart, i);
      colonne.push(d);
    }
  }

  const colW = modo === "settimana" ? 80 : 44;

  // Calcola posizione e larghezza barra per una fase
  function getBarStyle(fase) {
    if (!fase.inizio || !fase.fine || colonne.length === 0) return null;
    const fInizio = parseDate(fase.inizio);
    const fFine = addDays(parseDate(fase.fine), 1); // fine inclusiva
    const viewEnd = modo === "settimana"
      ? addDays(colonne[colonne.length-1], 7)
      : addDays(colonne[colonne.length-1], 1);

    if (fFine <= colonne[0] || fInizio >= viewEnd) return null;

    const totalMs = modo === "settimana" ? 7*24*3600*1000 : 24*3600*1000;
    const startOffset = Math.max(0, (fInizio - colonne[0]) / totalMs);
    const endOffset = Math.min(cols, (fFine - colonne[0]) / totalMs);
    const width = endOffset - startOffset;

    return {
      left: startOffset * colW,
      width: Math.max(width * colW - 2, colW - 2),
    };
  }

  // Stampa PDF cronoprogramma
  const stampaPDF = () => {
    const win = window.open("","_blank");
    const oggi = new Date();
    const dataConsStr = project?.dataFine ? new Date(project.dataFine+"T00:00:00").toLocaleDateString("it-IT",{day:"2-digit",month:"long",year:"numeric"}) : "—";
    const dataOrigStr = project?.dataConsegnaOriginale && project.dataConsegnaOriginale !== project.dataFine
      ? new Date(project.dataConsegnaOriginale+"T00:00:00").toLocaleDateString("it-IT",{day:"2-digit",month:"long",year:"numeric"})
      : null;

    // Calcola timeline complessiva
    const dates = fasi.map(f=>f.inizio).filter(Boolean).sort();
    const datesFine = fasi.map(f=>f.fine).filter(Boolean).sort();
    const primaData = dates[0] ? new Date(dates[0]+"T00:00:00") : oggi;
    const ultimaData = datesFine[datesFine.length-1] ? new Date(datesFine[datesFine.length-1]+"T00:00:00") : oggi;
    const durataGg = Math.max(1, Math.round((ultimaData-primaData)/(24*3600*1000))+1);

    // Genera colonne settimanali per il Gantt
    const settimane = [];
    let cur = new Date(primaData);
    // porta a lunedì
    const dow = cur.getDay();
    cur.setDate(cur.getDate() - (dow===0?6:dow-1));
    while(cur <= ultimaData) {
      settimane.push(new Date(cur));
      cur = new Date(cur);
      cur.setDate(cur.getDate()+7);
    }

    const fmtD = d => d.toLocaleDateString("it-IT",{day:"2-digit",month:"short"});
    const durFase = f => {
      if(!f.inizio||!f.fine) return "—";
      const d = Math.round((new Date(f.fine+"T00:00:00")-new Date(f.inizio+"T00:00:00"))/(24*3600*1000))+1;
      return d+" gg";
    };

    // Calcola posizione barra Gantt (% sulla timeline)
    const ganttBar = f => {
      if(!f.inizio||!f.fine) return null;
      const start = new Date(f.inizio+"T00:00:00");
      const end = new Date(f.fine+"T00:00:00");
      const left = Math.max(0, (start-primaData)/((ultimaData-primaData)||1)*100);
      const width = Math.max(1, (end-start)/((ultimaData-primaData)||1)*100);
      return {left: left.toFixed(1), width: Math.min(width, 100-left).toFixed(1)};
    };

    const logoHtml = orgInfo?.logo
      ? `<img src="${orgInfo.logo}" style="height:44px;object-fit:contain;margin-bottom:4px;" />`
      : `<div style="font-size:20px;font-weight:900;color:#1e1b4b;">◆ ${orgInfo?.nome||orgInfo?.name||"Nexiora"}</div>`;

    win.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"/>
    <title>Cronoprogramma — ${project?.name||""}</title>
    <style>
      *{margin:0;padding:0;box-sizing:border-box;}
      body{font-family:Arial,sans-serif;font-size:11px;color:#111;padding:16mm 20mm;}
      @media screen{body{max-width:800px;margin:0 auto;background:#fff;box-shadow:0 2px 16px rgba(0,0,0,.08);}}
      .header{border-bottom:3px solid #1e1b4b;padding-bottom:10px;margin-bottom:16px;display:flex;justify-content:space-between;align-items:flex-start;}
      .logo h1{font-size:18px;color:#1e1b4b;font-weight:900;}
      .logo p{font-size:10px;color:#666;}
      .title-area{text-align:right;}
      .title-area h2{font-size:14px;color:#1e1b4b;font-weight:800;}
      .title-area p{font-size:10px;color:#666;}
      .badge{display:inline-block;padding:3px 10px;border-radius:20px;font-size:10px;font-weight:700;margin-top:3px;}
      .badge-green{background:#d1fae5;color:#065f46;}
      .badge-red{background:#fee2e2;color:#991b1b;}
      .info-grid{display:grid;grid-template-columns:1fr 1fr 1fr 1fr;gap:10px;margin-bottom:16px;}
      .info-box{background:#f9fafb;border:1px solid #e5e7eb;border-radius:6px;padding:8px 10px;}
      .info-label{font-size:9px;color:#9ca3af;font-weight:700;text-transform:uppercase;margin-bottom:3px;}
      .info-value{font-size:12px;font-weight:700;color:#111;}
      h3{font-size:11px;font-weight:800;color:#1e1b4b;text-transform:uppercase;letter-spacing:.04em;background:#f3f4f6;padding:5px 8px;border-radius:4px;margin-bottom:8px;}
      table{width:100%;border-collapse:collapse;margin-bottom:16px;}
      th{background:#1e1b4b;color:#fff;padding:6px 8px;font-size:9px;font-weight:700;text-align:left;}
      td{padding:5px 8px;border-bottom:1px solid #f3f4f6;font-size:10px;}
      tr:nth-child(even) td{background:#fafafa;}
      .color-dot{display:inline-block;width:10px;height:10px;border-radius:3px;margin-right:5px;vertical-align:middle;}
      .gantt-section{margin-bottom:16px;}
      .gantt-row{display:flex;align-items:center;margin-bottom:4px;gap:8px;}
      .gantt-label{width:120px;font-size:9px;font-weight:600;color:#374151;flex-shrink:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
      .gantt-track{flex:1;background:#f3f4f6;border-radius:4px;height:14px;position:relative;overflow:hidden;}
      .gantt-bar{position:absolute;top:0;height:100%;border-radius:4px;opacity:.85;}
      .gantt-consegna{position:absolute;top:-2px;bottom:-2px;width:2px;background:#dc2626;z-index:2;}
      .footer{border-top:1px solid #e5e7eb;padding-top:8px;margin-top:8px;font-size:9px;color:#9ca3af;display:flex;justify-content:space-between;}
      @media print{body{padding:10mm 12mm;}}
    </style></head><body>

    <div class="header">
      <div class="logo">
        ${logoHtml}
        <div style="font-size:11px;font-weight:700;color:#374151;margin-top:2px;">${orgInfo?.nome||orgInfo?.name||""}</div>
        ${orgInfo?.indirizzo?`<div style="font-size:9px;color:#6b7280;">${orgInfo.indirizzo}</div>`:""}
        ${orgInfo?.piva?`<div style="font-size:9px;color:#6b7280;">P.IVA ${orgInfo.piva}</div>`:""}
      </div>
      <div class="title-area">
        <h2>${project?.codiceCommessa?`[${project.codiceCommessa}] `:""}${project?.name||"Commessa"}</h2>
        <p>${project?.clienteNome||""}</p>
        <span class="badge ${oggi > (project?.dataFine?new Date(project.dataFine+"T00:00:00"):oggi) ? "badge-red":"badge-green"}">
          Consegna: ${dataConsStr}
        </span>
        ${dataOrigStr?`<br/><span style="font-size:9px;color:#f59e0b;">Data originale: ${dataOrigStr}</span>`:""}
      </div>
    </div>

    <div class="info-grid">
      <div class="info-box"><div class="info-label">Fasi totali</div><div class="info-value">${fasi.length}</div></div>
      <div class="info-box"><div class="info-label">Inizio lavori</div><div class="info-value">${dates[0]?new Date(dates[0]+"T00:00:00").toLocaleDateString("it-IT"):"—"}</div></div>
      <div class="info-box"><div class="info-label">Fine prevista</div><div class="info-value">${datesFine[datesFine.length-1]?new Date(datesFine[datesFine.length-1]+"T00:00:00").toLocaleDateString("it-IT"):"—"}</div></div>
      <div class="info-box"><div class="info-label">Durata totale</div><div class="info-value">${durataGg} giorni</div></div>
    </div>

    <h3>📊 Diagramma di Gantt</h3>
    <div class="gantt-section">
      ${fasi.map(f=>{
        const bar = ganttBar(f);
        const consLeft = project?.dataFine && primaData && ultimaData ?
          ((new Date(project.dataFine+"T00:00:00")-primaData)/((ultimaData-primaData)||1)*100).toFixed(1) : null;
        return `<div class="gantt-row">
          <div class="gantt-label"><span class="color-dot" style="background:${f.colore||"#7c3aed"}"></span>${f.nome}</div>
          <div class="gantt-track">
            ${bar?`<div class="gantt-bar" style="left:${bar.left}%;width:${bar.width}%;background:${f.colore||"#7c3aed"}"></div>`:""}
            ${consLeft?`<div class="gantt-consegna" style="left:${consLeft}%"></div>`:""}
          </div>
          <div style="font-size:9px;color:#9ca3af;flex-shrink:0;width:60px;text-align:right">${f.inizio?fmtD(new Date(f.inizio+"T00:00:00")):""}${f.fine?" → "+fmtD(new Date(f.fine+"T00:00:00")):""}</div>
        </div>`;
      }).join("")}
      <div style="font-size:9px;color:#dc2626;margin-top:4px;">— linea rossa = data di consegna prevista</div>
    </div>

    <h3>📋 Dettaglio fasi</h3>
    <table>
      <thead><tr>
        <th>#</th><th>Fase</th><th>Inizio</th><th>Fine</th><th>Durata</th><th>Descrizione</th>
      </tr></thead>
      <tbody>
        ${fasi.map((f,i)=>`<tr>
          <td><span class="color-dot" style="background:${f.colore||"#7c3aed"}"></span>${i+1}</td>
          <td style="font-weight:700">${f.nome}</td>
          <td>${f.inizio?new Date(f.inizio+"T00:00:00").toLocaleDateString("it-IT"):"—"}</td>
          <td>${f.fine?new Date(f.fine+"T00:00:00").toLocaleDateString("it-IT"):"—"}</td>
          <td>${durFase(f)}</td>
          <td style="color:#6b7280">${f.descrizione||""}</td>
        </tr>`).join("")}
      </tbody>
    </table>

    <div class="footer">
      <span>Generato da Nexiora — ${dominioSito()}</span>
      <span>Stampato il ${oggi.toLocaleDateString("it-IT")} alle ${oggi.toLocaleTimeString("it-IT",{hour:"2-digit",minute:"2-digit"})}</span>
    </div>
    
    </body></html>`);
    win.document.close();
    setTimeout(()=>{
      if (win.closed) return;
      const style = win.document.createElement("style");
      style.textContent = "@media print { .no-print-btn { display:none !important; } }";
      win.document.head.appendChild(style);
      const btn = win.document.createElement("button");
      btn.textContent = "🖨️ Stampa";
      btn.className = "no-print-btn";
      btn.onclick = () => win.print();
      btn.style.cssText = "position:fixed;top:16px;right:16px;z-index:9999;padding:10px 20px;background:#7c3aed;color:#fff;border:none;border-radius:8px;font-size:14px;font-weight:700;cursor:pointer;font-family:Arial,sans-serif;box-shadow:0 4px 12px rgba(0,0,0,.2);";
      win.document.body.appendChild(btn);
    }, 50);
  };

  // Salva nuova data consegna
  const salvaDataConsegna = async () => {
    if (!nuovaDataConsegna) return;
    const updates = { dataFine: nuovaDataConsegna };
    // Memorizza la data originale solo la prima volta
    if (!project?.dataConsegnaOriginale) {
      updates.dataConsegnaOriginale = project?.dataFine || nuovaDataConsegna;
    }
    await updateDoc(doc(db,"projects",projectId), updates);
    setEditConsegna(false);
  };

  // Salva fase
  const salvaFase = async (fase) => {
    if (!fase.nome.trim() || !fase.inizio || !fase.fine) return;
    if (fase.id) {
      await updateDoc(doc(db,"cronoprogrammaFasi",fase.id), { ...fase, updatedAt: serverTimestamp() });
    } else {
      await addDoc(collection(db,"cronoprogrammaFasi"), {
        ...fase, projectId, orgId, createdAt: serverTimestamp()
      });
    }
    setShowAddFase(false);
    setEditFase(null);
    setNuovaFase({ nome:"", inizio:"", fine:"", colore: COLORI_FASE[0], descrizione:"" });
  };

  const eliminaFase = async (id) => {
    await deleteDoc(doc(db,"cronoprogrammaFasi",id));
  };

  // Salva nota
  const salvaNota = async () => {
    if (!notaTesto.trim() || !notaAperta) return;
    await addDoc(collection(db,"cronoprogrammaNote"), {
      projectId, orgId,
      faseId: notaAperta.faseId,
      data: notaAperta.data,
      testo: notaTesto.trim(),
      autore: currentUser?.displayName || "Utente",
      autorId: currentUser?.uid,
      createdAt: serverTimestamp()
    });
    setNotaTesto("");
  };

  const eliminaNota = async (id) => {
    await deleteDoc(doc(db,"cronoprogrammaNote",id));
  };

  const navigate = (dir) => {
    if (!viewStart) return;
    const step = modo === "settimana" ? cols * 7 : cols;
    setViewStart(addDays(viewStart, dir * step));
  };

  // Calcola data fine massima tra tutte le fasi
  const maxFase = fasi.map(f=>f.fine).filter(Boolean).sort().pop();
  const dataFineProgramma = maxFase ? parseDate(maxFase) : null;

  return (
    <div style={{ display:"flex", flexDirection:"column", height:"100%", overflow:"hidden", background:"#f9fafb" }}>

      {/* Alert consegna superata */}
      {consegnaSupeata && (
        <div style={{ background:"#fef2f2", borderBottom:"2px solid #dc2626", padding:"8px 20px", display:"flex", alignItems:"center", gap:10 }}>
          <span style={{ fontSize:16 }}>🚨</span>
          <span style={{ fontSize:13, fontWeight:700, color:"#dc2626" }}>
            Data di consegna superata — prevista il {dataConsegna.toLocaleDateString("it-IT",{day:"2-digit",month:"long",year:"numeric"})}
          </span>
        </div>
      )}

      {/* Header */}
      <div style={{ background:"#fff", borderBottom:"1px solid #e5e7eb", padding:"12px 20px", display:"flex", alignItems:"center", gap:12, flexWrap:"wrap", flexShrink:0 }}>

        {/* Data consegna — modificabile */}
        <div style={{ display:"flex", flexDirection:"column", gap:3 }}>
          {editConsegna ? (
            <div style={{ display:"flex", alignItems:"center", gap:6 }}>
              <input type="date" value={nuovaDataConsegna}
                onChange={e=>setNuovaDataConsegna(e.target.value)}
                style={{ padding:"4px 8px", border:"1px solid #7c3aed", borderRadius:7, fontSize:12, fontFamily:"'Inter',sans-serif" }}/>
              <button onClick={salvaDataConsegna}
                style={{ padding:"4px 10px", border:"none", borderRadius:7, background:"#7c3aed", color:"#fff", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
                ✓ Salva
              </button>
              <button onClick={()=>setEditConsegna(false)}
                style={{ padding:"4px 8px", border:"1px solid #e5e7eb", borderRadius:7, background:"#fff", fontSize:12, cursor:"pointer" }}>
                ✕
              </button>
            </div>
          ) : (
            <div style={{ display:"flex", alignItems:"center", gap:6, padding:"5px 12px",
              background: consegnaSupeata ? "#fef2f2" : "#f0fdf4",
              border: `2px solid ${consegnaSupeata ? "#fca5a5" : "#6ee7b7"}`,
              borderRadius:8, cursor: puoModificare ? "pointer" : "default" }}
              onClick={()=>{ if(!puoModificare) return; setNuovaDataConsegna(project?.dataFine||""); setEditConsegna(true); }}
              title={puoModificare ? "Clicca per modificare la data di consegna" : undefined}>
              <span style={{ fontSize:12, color: consegnaSupeata ? "#dc2626" : "#059669", fontWeight:700 }}>
                {consegnaSupeata ? "⚠️" : "📅"} Consegna:
              </span>
              <span style={{ fontSize:13, fontWeight:800, color: consegnaSupeata ? "#dc2626" : "#065f46" }}>
                {dataConsegna ? dataConsegna.toLocaleDateString("it-IT",{day:"2-digit",month:"short",year:"numeric"}) : "—"}
              </span>
              {puoModificare && <span style={{ fontSize:10, color:"#9ca3af" }}>✏️</span>}
            </div>
          )}
          {/* Data originale se modificata */}
          {consegnaModificata && dataConsegnaOriginale && !editConsegna && (
            <div style={{ fontSize:10, color:"#9ca3af", paddingLeft:4 }}>
              Originale: {dataConsegnaOriginale.toLocaleDateString("it-IT",{day:"2-digit",month:"short",year:"numeric"})}
              {oggi > dataConsegnaOriginale && <span style={{ color:"#f59e0b", marginLeft:4, fontWeight:700 }}>⚠️ superata</span>}
            </div>
          )}
        </div>

        {/* Fine programma */}
        {dataFineProgramma && dataConsegna && dataFineProgramma > dataConsegna && (
          <div style={{ fontSize:12, color:"#f59e0b", fontWeight:600, background:"#fffbeb", padding:"4px 10px", borderRadius:6, border:"1px solid #fde68a" }}>
            ⚠️ Cronoprogramma esteso oltre la consegna ({dataFineProgramma.toLocaleDateString("it-IT",{day:"2-digit",month:"short"})})
          </div>
        )}

        <div style={{ marginLeft:"auto", display:"flex", gap:8, alignItems:"center" }}>
          {/* Toggle modo */}
          <div style={{ display:"flex", background:"#f3f4f6", borderRadius:7, border:"1px solid #e5e7eb" }}>
            {[{id:"settimana",label:"Settimane"},{id:"giorno",label:"Giorni"}].map(m=>(
              <button key={m.id} onClick={()=>{
                const dates = fasi.map(f=>f.inizio).filter(Boolean).sort();
                const base = dates.length>0 ? parseDate(dates[0]) : (project?.dataInizio ? parseDate(project.dataInizio) : new Date());
                setModo(m.id);
                setViewStart(m.id==="settimana" ? startOfWeek(base) : base);
              }}
                style={{ padding:"5px 12px", border:"none", borderRadius:7,
                  background:modo===m.id?"#7c3aed":"transparent",
                  color:modo===m.id?"#fff":"#6b7280", fontSize:12, fontWeight:modo===m.id?700:400,
                  cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
                {m.label}
              </button>
            ))}
          </div>

          {/* Navigazione */}
          <button onClick={()=>navigate(-1)} style={{ padding:"5px 8px", border:"1px solid #e5e7eb", borderRadius:7, background:"#fff", cursor:"pointer" }}>
            <ChevronLeft size={14}/>
          </button>
          <button onClick={()=>{ setViewStart(modo==="settimana" ? startOfWeek(new Date()) : new Date()); }}
            style={{ padding:"5px 10px", border:"1px solid #e5e7eb", borderRadius:7, background:"#fff", fontSize:11, fontWeight:600, color:"#374151", cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
            Oggi
          </button>
          <button onClick={()=>navigate(1)} style={{ padding:"5px 8px", border:"1px solid #e5e7eb", borderRadius:7, background:"#fff", cursor:"pointer" }}>
            <ChevronRight size={14}/>
          </button>

          {/* Zoom */}
          <select value={cols} onChange={e=>setCols(Number(e.target.value))}
            style={{ padding:"5px 8px", border:"1px solid #e5e7eb", borderRadius:7, fontSize:12, fontFamily:"'Inter',sans-serif", cursor:"pointer" }}>
            <option value={8}>8 {modo==="settimana"?"sett.":"giorni"}</option>
            <option value={12}>12</option>
            <option value={16}>16</option>
            <option value={24}>24</option>
          </select>

          {/* Aggiungi fase */}
          {fasi.length > 0 && (
            <button onClick={stampaPDF}
              style={{ display:"flex", alignItems:"center", gap:5, padding:"6px 14px",
                border:"1px solid #2563eb", borderRadius:8, background:"#dbeafe", color:"#1d4ed8",
                fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
              <Printer size={13}/> Stampa PDF
            </button>
          )}
          {puoModificare && (
            <button onClick={()=>setShowAddFase(true)}
              style={{ display:"flex", alignItems:"center", gap:5, padding:"6px 14px",
                border:"none", borderRadius:8, background:"#7c3aed", color:"#fff",
                fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
              <Plus size={13}/> Nuova fase
            </button>
          )}
        </div>
      </div>

      {/* Griglia */}
      <div ref={containerRef} style={{ flex:1, overflow:"auto" }}>
        {fasi.length === 0 && !showAddFase && (
          <div style={{ textAlign:"center", padding:60, color:"#9ca3af" }}>
            <div style={{ fontSize:40, marginBottom:12 }}>📅</div>
            <div style={{ fontSize:14, fontWeight:600, marginBottom:6 }}>Nessuna fase pianificata</div>
            {puoModificare && <div style={{ fontSize:12 }}>Clicca «Nuova fase» per iniziare a costruire il cronoprogramma</div>}
          </div>
        )}

        {fasi.length > 0 && (
          <div style={{ minWidth: 200 + colonne.length * colW }}>
            {/* Intestazione colonne */}
            <div style={{ display:"flex", borderBottom:"2px solid #e5e7eb", background:"#fff", position:"sticky", top:0, zIndex:10 }}>
              <div style={{ width:200, minWidth:200, padding:"10px 14px", fontSize:11, fontWeight:700, color:"#6b7280", borderRight:"1px solid #e5e7eb", flexShrink:0 }}>
                FASE
              </div>
              {colonne.map((d, i) => {
                const isOggi = formatDate(d) === formatDate(oggi);
                const isConsegna = dataConsegna && formatDate(d) === formatDate(dataConsegna);
                const isConsegnaOrig = consegnaModificata && dataConsegnaOriginale && formatDate(d) === formatDate(dataConsegnaOriginale);
                return (
                  <div key={i} style={{ width:colW, minWidth:colW, textAlign:"center", padding:"4px 2px",
                    background: isConsegnaOrig ? "#fffbeb" : isConsegna ? (consegnaSupeata?"#fef2f2":"#f0fdf4") : isOggi ? "#ede9fe" : "transparent",
                    borderRight:"1px solid #f3f4f6", flexShrink:0 }}>
                    <div style={{ fontSize:10, fontWeight: isOggi||isConsegna ? 800 : 500,
                      color: isConsegna ? (consegnaSupeata?"#dc2626":"#059669") : isOggi ? "#7c3aed" : "#9ca3af" }}>
                      {modo==="settimana"
                        ? `Sett. ${Math.ceil((d - new Date(d.getFullYear(),0,1))/(7*24*3600*1000))}`
                        : ["Dom","Lun","Mar","Mer","Gio","Ven","Sab"][d.getDay()]}
                    </div>
                    <div style={{ fontSize:11, fontWeight: isOggi||isConsegna ? 800 : 400,
                      color: isConsegna ? (consegnaSupeata?"#dc2626":"#059669") : isOggi ? "#7c3aed" : "#374151" }}>
                      {formatLabel(d, modo)}
                      {isConsegnaOrig && !isConsegna && <div style={{ fontSize:9, fontWeight:700, color:"#f59e0b" }}>📅 ORIG.</div>}
                      {isConsegna && <div style={{ fontSize:9, fontWeight:800 }}>📅 CONS.</div>}
                      {isOggi && !isConsegna && <div style={{ fontSize:9, color:"#7c3aed" }}>oggi</div>}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Righe fasi */}
            {fasi.map((fase, idx) => {
              const bar = getBarStyle(fase);
              return (
                <div key={fase.id} style={{ display:"flex", borderBottom:"1px solid #f3f4f6",
                  background: idx%2===0 ? "#fff" : "#fafafa", minHeight:52 }}>

                  {/* Nome fase */}
                  <div style={{ width:200, minWidth:200, padding:"8px 12px", borderRight:"1px solid #e5e7eb",
                    display:"flex", alignItems:"center", gap:6, flexShrink:0 }}>
                    <div style={{ width:10, height:10, borderRadius:3, background:fase.colore||"#7c3aed", flexShrink:0 }}/>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:12, fontWeight:700, color:"#111827", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                        {fase.nome}
                      </div>
                      {fase.inizio && fase.fine && (
                        <div style={{ fontSize:10, color:"#9ca3af" }}>
                          {parseDate(fase.inizio).toLocaleDateString("it-IT",{day:"2-digit",month:"short"})} → {parseDate(fase.fine).toLocaleDateString("it-IT",{day:"2-digit",month:"short"})}
                        </div>
                      )}
                    </div>
                    {puoModificare && (
                      <div style={{ display:"flex", gap:3, flexShrink:0 }}>
                        <Edit2 size={11} color="#9ca3af" style={{ cursor:"pointer" }}
                          onClick={()=>setEditFase({...fase})}/>
                        <Trash2 size={11} color="rgba(220,38,38,0.5)" style={{ cursor:"pointer" }}
                          onClick={()=>eliminaFase(fase.id)}/>
                      </div>
                    )}
                  </div>

                  {/* Barra Gantt */}
                  <div style={{ flex:1, position:"relative", overflow:"hidden" }}>
                    {/* Colonne di sfondo */}
                    {colonne.map((d, i) => {
                      const isOggi = formatDate(d) === formatDate(oggi);
                      const isConsegna = dataConsegna && formatDate(d) === formatDate(dataConsegna);
                      const isConsegnaOrigGantt = consegnaModificata && dataConsegnaOriginale && formatDate(d) === formatDate(dataConsegnaOriginale);
                      const key = `${fase.id}_${formatDate(d)}`;
                      const noteGiorno = note[key] || [];
                      return (
                        <div key={i} style={{ position:"absolute", left:i*colW, top:0, width:colW, height:"100%",
                          background: isConsegnaOrigGantt ? "rgba(245,158,11,0.08)" :
                            isConsegna ? (consegnaSupeata?"rgba(220,38,38,0.05)":"rgba(5,150,105,0.05)") :
                            isOggi ? "rgba(124,58,237,0.05)" : "transparent",
                          borderRight:"1px solid #f3f4f6",
                          borderLeft: isConsegnaOrigGantt ? "2px dashed #f59e0b" : "none" }}>
                          {/* Indicatore note */}
                          {noteGiorno.length > 0 && (
                            <div onClick={()=>{ setNotaAperta({faseId:fase.id, data:formatDate(d)}); setNotaTesto(""); }}
                              style={{ position:"absolute", bottom:3, right:3, width:16, height:16,
                                background:"#f59e0b", borderRadius:"50%", display:"flex", alignItems:"center",
                                justifyContent:"center", cursor:"pointer", zIndex:3 }}>
                              <span style={{ fontSize:9, color:"#fff", fontWeight:800 }}>{noteGiorno.length}</span>
                            </div>
                          )}
                          {/* Clic per aggiungere nota */}
                          <div onClick={()=>{ setNotaAperta({faseId:fase.id, data:formatDate(d)}); setNotaTesto(""); }}
                            style={{ position:"absolute", inset:0, cursor:"pointer", zIndex:1 }}
                            title="Clicca per aggiungere nota"/>
                        </div>
                      );
                    })}

                    {/* Barra fase */}
                    {bar && (
                      <div style={{ position:"absolute", left:bar.left+2, top:"50%", transform:"translateY(-50%)",
                        width:bar.width, height:28, background:fase.colore||"#7c3aed",
                        borderRadius:6, opacity:0.85, zIndex:2, display:"flex", alignItems:"center",
                        padding:"0 8px", overflow:"hidden" }}>
                        <span style={{ fontSize:11, fontWeight:700, color:"#fff", whiteSpace:"nowrap",
                          overflow:"hidden", textOverflow:"ellipsis" }}>
                          {fase.nome}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Modal aggiungi/modifica fase */}
      {(showAddFase || editFase) && puoModificare && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.5)", zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center" }}>
          <div style={{ background:"#fff", borderRadius:16, padding:24, width:440, boxShadow:"0 20px 60px rgba(0,0,0,0.3)" }}>
            <div style={{ fontSize:16, fontWeight:800, color:"#1e1b4b", marginBottom:16 }}>
              {editFase ? "Modifica fase" : "Nuova fase"}
            </div>
            {[
              { label:"Nome fase *", key:"nome", type:"text", placeholder:"Es. Installazione quadri" },
              { label:"Data inizio *", key:"inizio", type:"date" },
              { label:"Data fine *", key:"fine", type:"date" },
              { label:"Descrizione", key:"descrizione", type:"text", placeholder:"Note opzionali" },
            ].map(f => (
              <div key={f.key} style={{ marginBottom:12 }}>
                <label style={{ fontSize:11, fontWeight:700, color:"#6b7280", display:"block", marginBottom:4 }}>{f.label}</label>
                <input type={f.type} placeholder={f.placeholder}
                  value={editFase ? editFase[f.key]||"" : nuovaFase[f.key]||""}
                  onChange={e => editFase
                    ? setEditFase(p=>({...p,[f.key]:e.target.value}))
                    : setNuovaFase(p=>({...p,[f.key]:e.target.value}))}
                  style={{ width:"100%", padding:"8px 10px", border:"1px solid #e5e7eb", borderRadius:8,
                    fontSize:13, fontFamily:"'Inter',sans-serif", boxSizing:"border-box" }}/>
              </div>
            ))}
            {/* Colore */}
            <div style={{ marginBottom:16 }}>
              <label style={{ fontSize:11, fontWeight:700, color:"#6b7280", display:"block", marginBottom:6 }}>Colore</label>
              <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                {COLORI_FASE.map(c => (
                  <div key={c} onClick={()=> editFase ? setEditFase(p=>({...p,colore:c})) : setNuovaFase(p=>({...p,colore:c}))}
                    style={{ width:24, height:24, borderRadius:6, background:c, cursor:"pointer",
                      border: (editFase?editFase.colore:nuovaFase.colore)===c ? "3px solid #111" : "3px solid transparent" }}/>
                ))}
              </div>
            </div>
            <div style={{ display:"flex", gap:8, justifyContent:"flex-end" }}>
              <button onClick={()=>{ setShowAddFase(false); setEditFase(null); }}
                style={{ padding:"8px 18px", border:"1px solid #e5e7eb", borderRadius:8, background:"#fff",
                  color:"#374151", fontSize:13, cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
                Annulla
              </button>
              <button onClick={()=>salvaFase(editFase||nuovaFase)}
                style={{ padding:"8px 18px", border:"none", borderRadius:8, background:"#7c3aed",
                  color:"#fff", fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:"'Inter',sans-serif" }}>
                {editFase ? "Salva modifiche" : "Crea fase"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pannello note */}
      {notaAperta && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.4)", zIndex:1000, display:"flex", alignItems:"flex-end", justifyContent:"flex-end" }}>
          <div style={{ background:"#fff", width:380, height:"100%", boxShadow:"-4px 0 30px rgba(0,0,0,0.15)",
            display:"flex", flexDirection:"column" }}>
            {/* Header pannello */}
            <div style={{ background:"#1e1b4b", padding:"16px 20px", display:"flex", alignItems:"center", gap:10 }}>
              <MessageSquare size={16} color="#c4b5fd"/>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:13, fontWeight:700, color:"#fff" }}>
                  {fasi.find(f=>f.id===notaAperta.faseId)?.nome || "Fase"}
                </div>
                <div style={{ fontSize:11, color:"rgba(255,255,255,0.5)" }}>
                  {parseDate(notaAperta.data).toLocaleDateString("it-IT",{weekday:"long",day:"numeric",month:"long"})}
                </div>
              </div>
              <button onClick={()=>setNotaAperta(null)}
                style={{ background:"none", border:"none", cursor:"pointer", color:"rgba(255,255,255,0.6)" }}>
                <X size={16}/>
              </button>
            </div>

            {/* Lista note */}
            <div style={{ flex:1, overflowY:"auto", padding:16 }}>
              {(note[`${notaAperta.faseId}_${notaAperta.data}`]||[]).length === 0 && (
                <div style={{ textAlign:"center", padding:30, color:"#9ca3af" }}>
                  <MessageSquare size={28} color="#e5e7eb" style={{ marginBottom:8 }}/>
                  <div style={{ fontSize:13 }}>Nessuna nota per questo giorno</div>
                </div>
              )}
              {(note[`${notaAperta.faseId}_${notaAperta.data}`]||[]).map(n => (
                <div key={n.id} style={{ background:"#f9fafb", borderRadius:10, padding:"10px 12px", marginBottom:8,
                  border:"1px solid #e5e7eb" }}>
                  <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:6 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                      <div style={{ width:24, height:24, borderRadius:"50%", background:"#ede9fe",
                        display:"flex", alignItems:"center", justifyContent:"center",
                        fontSize:11, fontWeight:800, color:"#7c3aed" }}>
                        {n.autore?.[0]?.toUpperCase()||"U"}
                      </div>
                      <span style={{ fontSize:12, fontWeight:600, color:"#374151" }}>{n.autore}</span>
                    </div>
                    {(puoModificare || (n.autorId === currentUser?.uid && !commessaChiusa)) && (
                      <Trash2 size={12} color="rgba(220,38,38,0.5)" style={{ cursor:"pointer" }}
                        onClick={()=>eliminaNota(n.id)}/>
                    )}
                  </div>
                  <div style={{ fontSize:13, color:"#111827", lineHeight:1.5 }}>{n.testo}</div>
                  {n.createdAt?.toDate && (
                    <div style={{ fontSize:10, color:"#9ca3af", marginTop:4 }}>
                      {n.createdAt.toDate().toLocaleString("it-IT",{hour:"2-digit",minute:"2-digit"})}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Input nuova nota */}
            <div style={{ padding:"12px 16px", borderTop:"1px solid #e5e7eb", background:"#fff" }}>
              <textarea value={notaTesto} onChange={e=>setNotaTesto(e.target.value)}
                placeholder="Scrivi una nota, segnala un problema o proponi una soluzione..."
                rows={3}
                style={{ width:"100%", padding:"10px 12px", border:"1px solid #e5e7eb", borderRadius:10,
                  fontSize:13, fontFamily:"'Inter',sans-serif", resize:"none", boxSizing:"border-box",
                  outline:"none" }}/>
              <button onClick={salvaNota} disabled={!notaTesto.trim()}
                style={{ marginTop:8, width:"100%", padding:"9px", border:"none", borderRadius:8,
                  background: notaTesto.trim() ? "#7c3aed" : "#e5e7eb",
                  color: notaTesto.trim() ? "#fff" : "#9ca3af",
                  fontSize:13, fontWeight:700, cursor: notaTesto.trim() ? "pointer" : "not-allowed",
                  fontFamily:"'Inter',sans-serif" }}>
                Aggiungi nota
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
